import React, { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFavourites, deleteFavourite, updateFavourite, reorderFavourites } from "@/data-access/favourites";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoaderCircle, AlertCircle, Heart, ExternalLink, Trash2, Edit, ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
function SortableItem({ favourite, onEdit, onDelete }: {
  favourite: any,
  onEdit: (favourite: { id: number; name: string }) => void,
  onDelete: (id: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: favourite.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`border border-border hover:shadow-md transition-shadow ${isDragging ? 'shadow-lg' : ''}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Drag Handle */}
            <div
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <Heart className="h-5 w-5 text-red-500 fill-current flex-shrink-0" />
                <h3 className="text-lg font-medium text-foreground truncate">
                  {favourite.name}
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                Added on {formatDate(favourite.createdAt.toString())}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            <Link href={favourite.url}>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(favourite)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(favourite.id)}
              className="flex items-center gap-2 text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FavouritesPage() {
  const queryClient = useQueryClient();
  const [editingFavourite, setEditingFavourite] = useState<{ id: number; name: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [isDragging, setIsDragging] = useState(false);

  // Drag sensors for better mobile support
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Query to get favourites with pagination
  const { 
    data: favouritesData, 
    isLoading, 
    isError, 
    error 
  } = useQuery({
    queryKey: ["favourites", currentPage, itemsPerPage],
    queryFn: () => getFavourites(currentPage, itemsPerPage),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to delete favourite
  const deleteFavouriteMutation = useMutation({
    mutationFn: deleteFavourite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favourites"] });
      setDeleteConfirmId(null);
      // If we deleted the last item on a page, go back to previous page
      if (favouritesData && favouritesData.favourites.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    },
  });

  // Mutation to update favourite
  const updateFavouriteMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => 
      updateFavourite(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favourites"] });
      setEditingFavourite(null);
      setEditName("");
    },
  });

  // Mutation to reorder favourites
  const reorderFavouritesMutation = useMutation({
    mutationFn: reorderFavourites,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favourites"] });
    },
  });

  const handleEditClick = (favourite: { id: number; name: string }) => {
    setEditingFavourite(favourite);
    setEditName(favourite.name);
  };

  const handleSaveEdit = () => {
    if (!editingFavourite || !editName.trim()) return;
    
    updateFavouriteMutation.mutate({
      id: editingFavourite.id,
      name: editName.trim(),
    });
  };

  const handleCancelEdit = () => {
    setEditingFavourite(null);
    setEditName("");
  };

  const handleDeleteClick = (id: number) => {
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      deleteFavouriteMutation.mutate(deleteConfirmId);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragging(false);

    if (!over || active.id === over.id || !favouritesData?.favourites) {
      return;
    }

    const oldIndex = favouritesData.favourites.findIndex(item => item.id === active.id);
    const newIndex = favouritesData.favourites.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedItems = arrayMove(favouritesData.favourites, oldIndex, newIndex);
    
    // Create the reorder payload with new order values
    const favouriteOrders = reorderedItems.map((item, index) => ({
      id: item.id,
      order: index
    }));

    // Update local state optimistically
    queryClient.setQueryData(
      ["favourites", currentPage, itemsPerPage],
      (oldData: any) => oldData ? {
        ...oldData,
        favourites: reorderedItems
      } : oldData
    );

    // Send the reorder request
    reorderFavouritesMutation.mutate(favouriteOrders);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="max-w-screen-lg mx-auto w-full">
        <div className="flex-grow pt-2 pb-2 px-0 sm:px-2 lg:px-4">
          <div className="space-y-4">
            <div className="mt-3 px-4">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  <h1 className="text-2xl font-bold text-foreground">My Favorites</h1>
                </div>
              </div>
            </div>

            {/* Loading State */}
            <div className="px-4">
              {isLoading && (
                <Card className="border border-border">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <LoaderCircle className="h-12 w-12 animate-spin text-primary" />
                    <p className="mt-4 text-muted-foreground font-medium">Loading your favorites...</p>
                  </CardContent>
                </Card>
              )}

              {/* Error State */}
              {isError && (
                <Card className="border border-border">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive mb-4">
                      <AlertCircle className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Error loading favorites</h3>
                    <p className="text-muted-foreground mb-4">
                      {error instanceof Error ? error.message : "An error occurred"}
                    </p>
                    <Button 
                      onClick={() => queryClient.invalidateQueries({ queryKey: ["favourites"] })}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Try Again
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Empty State */}
              {favouritesData && favouritesData.favourites.length === 0 && (
                <Card className="border border-border">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted text-primary mb-4">
                      <Heart className="h-8 w-8" />
                    </div>
                    <h3 className="text-lg font-medium text-foreground mb-2">No favorites yet</h3>
                    <p className="text-muted-foreground mb-4 text-center">
                      Start adding favorites by clicking the heart icon on any wallet address search.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Favourites List */}
              {favouritesData && favouritesData.favourites.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {favouritesData.totalCount} favorite{favouritesData.totalCount !== 1 ? 's' : ''}
                    </div>
                    
                    {/* Pagination Info */}
                    <div className="text-sm text-muted-foreground">
                      Page {favouritesData.currentPage} of {favouritesData.totalPages}
                    </div>
                  </div>
                
                {/* Drag and Drop Favourites */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  onDragStart={handleDragStart}
                >
                  <SortableContext
                    items={favouritesData.favourites.map(f => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {favouritesData.favourites.map((favourite) => (
                        <SortableItem
                          key={favourite.id}
                          favourite={favourite}
                          onEdit={handleEditClick}
                          onDelete={handleDeleteClick}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {/* Pagination Controls */}
                {favouritesData.totalPages > 1 && (
                  <Card className="border border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(favouritesData.currentPage - 1)}
                          disabled={favouritesData.currentPage === 1 || isLoading}
                          className="flex items-center gap-2"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        
                        <div className="flex items-center gap-2">
                          {Array.from({ length: favouritesData.totalPages }, (_, i) => i + 1).map((page) => {
                            // Show first, last, current, and 1 page before/after current
                            const shouldShow = 
                              page === 1 || 
                              page === favouritesData.totalPages || 
                              Math.abs(page - favouritesData.currentPage) <= 1;
                            
                            if (!shouldShow) {
                              // Show ellipsis for gaps
                              if (page === 2 && favouritesData.currentPage > 4) {
                                return <span key={page} className="text-muted-foreground">...</span>;
                              }
                              if (page === favouritesData.totalPages - 1 && favouritesData.currentPage < favouritesData.totalPages - 3) {
                                return <span key={page} className="text-muted-foreground">...</span>;
                              }
                              return null;
                            }
                            
                            return (
                              <Button
                                key={page}
                                variant={page === favouritesData.currentPage ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(page)}
                                disabled={isLoading}
                                className="w-10 h-10"
                              >
                                {page}
                              </Button>
                            );
                          })}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(favouritesData.currentPage + 1)}
                          disabled={favouritesData.currentPage === favouritesData.totalPages || isLoading}
                          className="flex items-center gap-2"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingFavourite} onOpenChange={(open) => !open && handleCancelEdit()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Favorite</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter a name for this favorite"
                className="focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:transition-none bg-background border border-input h-12 rounded-lg text-foreground placeholder:text-muted-foreground w-full transition-all duration-200"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelEdit}
              disabled={updateFavouriteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateFavouriteMutation.isPending || !editName.trim()}
            >
              {updateFavouriteMutation.isPending ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Favorite</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this favorite? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteFavouriteMutation.isPending}
            >
              {deleteFavouriteMutation.isPending ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 
